import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { queryKeys } from 'api/config';
import { produce } from 'immer';

import { IGoal } from '../types';

export const useGoalQueryActions = () => {
  const { goalId } = useParams();

  const queryClient = useQueryClient();

  const queryKey = useMemo(() => [queryKeys.goal, goalId], [goalId]);

  const cancelGoalQueries = useCallback(
    async () => await queryClient.cancelQueries(queryKey),
    [queryClient, queryKey]
  );

  const getGoalQuery = useCallback(
    () => queryClient.getQueryData<IGoal>(queryKey),
    [queryClient, queryKey]
  );

  const invalidateGoalQuery = useCallback(
    () => queryClient.invalidateQueries(queryKey),
    [queryClient, queryKey]
  );

  const updateGoalQueryData = useCallback(
    (updateFn: (draft: IGoal) => void) => {
      queryClient.setQueryData(
        queryKey,
        produce((draft: IGoal | undefined) => {
          if (!draft) {
            return {};
          }

          updateFn(draft);
        })
      );
    },
    [queryClient, queryKey]
  );

  return useMemo(
    () => ({
      cancelGoalQueries,
      getGoalQuery,
      updateGoalQueryData,
      invalidateGoalQuery,
    }),
    [cancelGoalQueries, getGoalQuery, updateGoalQueryData, invalidateGoalQuery]
  );
};
